import { Building2, HeartHandshake, Check } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Overline, PageHero, PrimaryLink } from "../components/Bits";
import { IMAGES } from "../lib/site";

const SERVICES = [
  {
    id: "rent-to-rent",
    icon: Building2,
    tag: "For landlords & property owners",
    title: "Rent-to-Rent",
    intro: "We take your property on a long-term lease and manage it completely — turning your asset into a truly hands-off investment.",
    image: IMAGES.terracedDoors,
    points: [
      "One agreed monthly rent on a 3–5 year lease",
      "No void-period hassle — we manage the whole term",
      "Simple, transparent 15% management fee",
      "Full maintenance and repairs handled by our team",
      "Routine professional inspections keep the property protected",
      "Property returned in excellent condition",
    ],
  },
  {
    id: "supported-housing",
    icon: HeartHandshake,
    tag: "For councils, housing associations & providers",
    title: "Supported Housing",
    intro: "Safe, high-quality accommodation for vulnerable people — delivered in partnership with local authorities and wrapped in genuine, round-the-clock support.",
    image: IMAGES.interiorDark,
    points: [
      "Accommodation for vulnerable and at-risk individuals",
      "24/7 tenant support and clear care pathways",
      "CQC & HMO compliant, Awaab's-Law-ready standards",
      "Rapid, responsible tenant placement",
      "Close partnership with local authority teams",
      "Ongoing safety, welfare and compliance monitoring",
    ],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        overline="Our services"
        titleLines={["Two ways we", "make housing work."]}
        subtitle="Long-term rent-to-rent for landlords, and compliant supported housing for councils and providers. Both delivered end-to-end, both held to the same high standard."
        image={IMAGES.interiorBright}
      />

      {SERVICES.map((s, idx) => (
        <section key={s.id} id={s.id} className="container-x py-16 lg:py-24" data-testid={`service-${s.id}`}>
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <Reveal>
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
                <img src={s.image} alt={s.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                <span className="absolute top-5 left-5 rounded-full glass px-4 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-amber">
                  {String(idx + 1).padStart(2, "0")} / {s.title}
                </span>
              </div>
            </Reveal>

            <div>
              <Reveal>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-brandcyan">{s.tag}</span>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-4 flex items-center gap-3">
                  <s.icon className="h-9 w-9 text-amber" />
                  <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">{s.title}</h2>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-5 text-slate-300 leading-relaxed text-base sm:text-lg">{s.intro}</p>
              </Reveal>
              <Stagger className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5" delay={0.1}>
                {s.points.map((p, i) => (
                  <StaggerItem key={i}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 grid place-items-center h-5 w-5 rounded-full bg-amber/15 shrink-0">
                        <Check className="h-3 w-3 text-amber" />
                      </span>
                      <span className="text-sm text-slate-300">{p}</span>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
              <div className="mt-10">
                <PrimaryLink to={s.id === "rent-to-rent" ? "/landlords" : "/local-authorities"} testid={`service-cta-${s.id}`}>
                  {s.id === "rent-to-rent" ? "For landlords" : "For local authorities"}
                </PrimaryLink>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="container-x pb-28 lg:pb-40">
        <Reveal>
          <div className="rounded-[2rem] card-surface p-10 lg:p-16 text-center">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white">Not sure which fits you?</h2>
            <p className="mt-4 text-slate-300 max-w-lg mx-auto">Tell us about your property or your placement needs and we'll point you in the right direction.</p>
            <div className="mt-8 flex justify-center"><PrimaryLink to="/contact" testid="services-cta">Talk to our team</PrimaryLink></div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
